import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Bell, BellRing } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

function urlB64ToUint8Array(b64) {
  const padding = "=".repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

async function ensureSubscribed() {
  if (!("serviceWorker" in navigator) || !("PushManager" in window)) return;
  if (Notification.permission === "denied") return;
  if (Notification.permission !== "granted") {
    const p = await Notification.requestPermission();
    if (p !== "granted") return;
  }
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const { data } = await api.get("/push/vapid-key");
    if (!data.public_key) return;
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlB64ToUint8Array(data.public_key),
    });
  }
  const j = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: j.endpoint, keys: j.keys });
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch {}
  };

  useEffect(() => {
    load();
    ensureSubscribed().catch(() => {});
    const t = setInterval(load, 45000);
    return () => clearInterval(t);
  }, []);

  const unread = items.filter((n) => !n.read).length;

  const openItem = async (n) => {
    try { await api.post("/notifications/read-all"); } catch {}
    load();
    nav(n.url || "/dashboard");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="relative" data-testid="notification-bell">
          {unread > 0 ? <BellRing className="w-4 h-4 text-amber-600"/> : <Bell className="w-4 h-4"/>}
          {unread > 0 && <span className="absolute -top-1 -right-1 bg-rose-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">{unread}</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-menu">
        <div className="px-3 py-2 border-b border-slate-200 flex items-center justify-between">
          <span className="text-sm font-semibold">Notifications</span>
          {unread > 0 && <button onClick={async ()=>{ await api.post("/notifications/read-all"); load();}} className="text-[10px] text-amber-600 font-semibold uppercase" data-testid="mark-all-read">Mark all read</button>}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 && <div className="px-3 py-8 text-center text-slate-400 text-sm">No notifications yet</div>}
          {items.map(n => (
            <button key={n.id} onClick={()=>openItem(n)} className={`w-full text-left px-3 py-2.5 border-b border-slate-100 hover:bg-slate-50 ${!n.read ? "bg-amber-50/40" : ""}`}>
              <div className="text-sm font-semibold text-slate-900">{n.title}</div>
              <div className="text-xs text-slate-600 mt-0.5">{n.body}</div>
              <div className="text-[10px] text-slate-400 mt-1">{new Date(n.created_at).toLocaleString("en-IN")}</div>
            </button>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
